import { jwtDecode } from 'jwt-decode';
import { CognitoIdentityServiceProvider } from 'aws-sdk';
import { getCognitoConfig } from '../Config/auth.config';
import { secureStorage_getItem, secureStorage_setItem } from '../utility/secureStorage';

export const decodeToken = (token) => {
    try {
        return jwtDecode(token);
    } catch (error) {
        return null;
    }
};

export const extractUserInfo = async (token) => {
    try {
        const decoded = decodeToken(token);

        if (!decoded) {
            throw new Error('Failed to decode token');
        }

        // Extract user info from the decoded token
        const username = decoded.email || decoded['cognito:username'];
        if (!username) {
            throw new Error('No username found in token');
        }

        // Create basic user info object
        const userInfo = {
            username: username,
            email: decoded.email,
            sub: decoded.sub,
            groups: decoded['cognito:groups'] || [],
            roles: decoded['custom:roles'] || [],
            status: 'pending_review' // Default status
        };

        try {
            const customAttributes = await fetchUserCustomAttributes();
            if (customAttributes) {
                // Filter out standard Cognito attributes that aren't custom
                const standardAttributes = ['sub', 'email_verified', 'email', 'username', 'phone_number', 'phone_number_verified'];
                const filteredCustomAttributes = Object.keys(customAttributes)
                    .filter(key => !standardAttributes.includes(key))
                    .reduce((obj, key) => {
                        obj[key] = customAttributes[key];
                        return obj;
                    }, {});

                // If we have actual custom attributes, user is approved
                if (Object.keys(filteredCustomAttributes).length > 0) {
                    return {
                        ...userInfo,
                        ...filteredCustomAttributes,
                        status: 'approved'
                    };
                } else {
                    // No custom attributes found, keep as pending_review
                    return {
                        ...userInfo,
                        status: 'pending_review'
                    };
                }
            }
        } catch (error) {
            // If we get NotAuthorizedException or similar, keep as pending_review
            if (error.code === 'NotAuthorizedException' ||
                error.message.includes('required scopes') ||
                error.message.includes('token')) {
                return {
                    ...userInfo,
                    status: 'pending_review'
                };
            }
            // For other errors, mark as error status
            return {
                ...userInfo,
                status: 'error',
                error: error.message
            };
        }

        // If we get here without custom attributes, user is pending review
        return {
            ...userInfo,
            status: 'pending_review'
        };
    } catch (error) {
        throw error;
    }
};

export const fetchUserCustomAttributes = async () => {
    try {
        const config = getCognitoConfig();
        const accessToken = secureStorage_getItem('accesstoken');
        if (!accessToken) {
            throw new Error('No access token found');
        }

        // Create Cognito service object
        const cognitoISP = new CognitoIdentityServiceProvider({
            region: 'us-east-1'
        });

        const userData = await cognitoISP.getUser({
            AccessToken: accessToken
        }).promise();

        if (!userData || !userData.UserAttributes) {
            throw new Error('No user attributes found');
        }

        // Convert array of attributes to an object
        const attributes = userData.UserAttributes.reduce((acc, attr) => {
            const key = attr.Name.replace('custom:', '');
            acc[key] = attr.Value;
            return acc;
        }, {});

        return attributes;
    } catch (error) {
        throw error;
    }
};

export const updateUserCustomAttributes = async (username, attributes) => {
    try {
        // Format attributes for the API
        const formattedAttributes = attributes.map(attr => ({
            Name: attr.name.startsWith('custom:') ? attr.name : `custom:${attr.name}`,
            Value: attr.value
        }));

        const payload = {
            userattr: formattedAttributes
        };

        const response = await apigClient.vlauthCognitocustomattributesUsernamePut(
            { username },
            payload
        );

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const storeUserSession = (code) => {
    if (!code) return null;

    const now = new Date().getTime();

    // Preserve existing values before updating
    const existingUserInfo = secureStorage_getItem('vlomni_user_info');
    const existingUsername = secureStorage_getItem('vlomni_login_username');
    const existingStatus = secureStorage_getItem('vlomni_user_status');
    const existingHubspot = secureStorage_getItem('hubspot_enabled');

    // Store the authentication code and session info
    secureStorage_setItem('vlomni_auth_code', code);
    secureStorage_setItem('vlomni_auth_timestamp', now.toString());
    secureStorage_setItem('vlomni_login_source', 'aws_cognito');
    secureStorage_setItem('vlomni_session_active', 'true');

    // Restore preserved values if they existed
    if (existingUserInfo) secureStorage_setItem('vlomni_user_info', existingUserInfo);
    if (existingUsername) secureStorage_setItem('vlomni_login_username', existingUsername);
    if (existingStatus) secureStorage_setItem('vlomni_user_status', existingStatus);
    if (existingHubspot) secureStorage_setItem('hubspot_enabled', existingHubspot);

    // Set session cookie
    document.cookie = `vlomni-cookie-active=${now};path=/;max-age=86400`; // 24 hours

    return true;
}; 